import merge from 'lodash/merge';
import ReactApexChart from 'react-apexcharts';
// @mui
import { Box, Card, CardProps, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { BaseOptionChart } from '../charts';
import { ColorSchema } from '../overrides/palette/palette';

// ----------------------------------------------------------------------

const RootStyle = styled(Card)(({ theme }) => ({
  display: 'flex',
  position: 'relative',
  alignItems: 'center',
  padding: theme.spacing(3),
  backgroundColor: theme.palette.primary.darker,
}));

// ----------------------------------------------------------------------

interface Props extends CardProps {
  icon: string;
  title: string;
  total: string;
  percentage: number;
  color?: ColorSchema;
  additionalInfo?: string;
  showPercentage?: boolean;
  setErrorColor?: boolean;
}

export const AppWidget: React.FC<Props> = ({
  title,
  total,
  color = 'primary',
  percentage,
  showPercentage = true,
  setErrorColor = false,
  additionalInfo,
  ...other
}) => {
  const theme = useTheme();
  let colorChart = color;

  if (percentage > 40) {
    colorChart = 'primary';
  }
  if (percentage > 80) {
    colorChart = 'warning';
  }
  if (percentage > 100 || setErrorColor) {
    colorChart = 'error';
  }

  const chartOptions = merge(BaseOptionChart(), {
    colors: [theme.palette[colorChart].main],
    chart: { sparkline: { enabled: true } },
    legend: { show: false },
    plotOptions: {
      radialBar: {
        hollow: { size: '78%' },
        track: { margin: 0 },
        dataLabels: {
          name: { show: false },
          value: {
            offsetY: 6,
            fontSize: theme.typography.subtitle2.fontSize,
            color: theme.palette[colorChart].main,
          },
        },
      },
    },
  });

  return (
    <RootStyle
      sx={{
        bgcolor: theme.palette.common.white,
        minHeight: '9rem',
      }}
      {...other}
    >
      {showPercentage && (
        <ReactApexChart
          type="radialBar"
          series={[percentage]}
          options={chartOptions}
          width={86}
          height={86}
        />
      )}

      <Box sx={{ ml: 3 }}>
        <Typography variant="h6" color={theme.palette[colorChart].main}>
          {title}
        </Typography>
        {additionalInfo && (
          <Typography variant="body2" sx={{ opacity: 0.82 }}>
            {additionalInfo}
          </Typography>
        )}
      </Box>
    </RootStyle>
  );
};

export default AppWidget;
